import { Component, AfterViewInit, ViewChild, ElementRef, Input, forwardRef } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDateFormats } from '@angular/material/core';
import { NgxMatDatetimepicker, NgxMatDateFormats } from '@imario42/datetime-picker';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { trim, isNil } from 'lodash-es';
import moment from 'moment';

interface DateBoxChangeTouchDelegate {
  (val: string): void;
}

@Component({
  selector: 'datex-common-datebox',
  templateUrl: './datebox.component.html',
  styleUrls: ['./datebox.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateboxComponent),
      multi: true
    }
  ],
  standalone: false
})
export class DateboxComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('input') input: ElementRef;
  @ViewChild('picker') picker: MatDatepicker<moment.Moment> | NgxMatDatetimepicker<moment.Moment>;

  @Input() mode = 'datetime';
  @Input() format = 'l, LT';
  @Input() placeholder = '';
  @Input() readOnly: boolean;

  disabled: boolean;
  formattedValue = null;
  _innerValue: string;


  ngAfterViewInit() {
    if (this.format) {
      // workaround until https://github.com/angular/components/issues/8355 is implemented
      Promise.resolve(null).then(() => {
        const dateFormats =
          //eslint-disable-next-line @typescript-eslint/no-explicit-any
          JSON.parse(JSON.stringify((this.picker.datepickerInput as any)._dateFormats)) as MatDateFormats | NgxMatDateFormats;
        dateFormats.parse.dateInput = this.format;
        dateFormats.display.dateInput = this.format;

        //eslint-disable-next-line @typescript-eslint/no-explicit-any
        const datepickerInput = this.picker.datepickerInput as any;
        datepickerInput._dateFormats = dateFormats;
        // Reformat the input base on datepickerInput._onBlur()
        if (datepickerInput.value) {
          datepickerInput._formatValue(datepickerInput.value);
        }
      });
    }
  }

  onValueChanged(val: moment.Moment) {
    const compareVal = this.isDefinedTrimmed(this.value) ? moment.utc(this.value) : null;
    if ((compareVal && val && !compareVal.isSame(val)) || (!compareVal && val) || (compareVal && !val)) {
      let parsedValue = null;
      if (val && val.isValid()) {
        parsedValue = val.toISOString();
      }
      this.value = parsedValue;
    }
  }

  onFocusOut() {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.onValueChanged((this.picker.datepickerInput as any).value);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.onValueChanged((this.picker.datepickerInput as any).value);
    }
  }

  isDefinedTrimmed(value) {
    if (typeof value === 'string') {
      value = trim(value);
    }
    const result = !isNil(value) && value !== '';
    return result;
  }

  onChange: DateBoxChangeTouchDelegate = () => void 0;
  onTouch: DateBoxChangeTouchDelegate = () => void 0;

  get value(): string {
    return this._innerValue;
  }

  set value(val: string) {
    this._innerValue = val;
    this.onChange(val);
    this.onTouch(val);
  }

  writeValue(value: string): void {
    this._innerValue = value;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: DateBoxChangeTouchDelegate): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: DateBoxChangeTouchDelegate): void {
    this.onTouch = fn;
  }
}
